import { useState, useEffect } from 'react';
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getAnnouncements();
      setAnnouncements(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newMessage.trim()) { toast.error('メッセージを入力してください'); return; }
    setSaving(true);
    try {
      await createAnnouncement(newMessage.trim());
      setNewMessage('');
      toast.success('お知らせを作成しました');
      fetchData();
    } catch (error) {
      toast.error('作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, currentActive) => {
    try {
      await updateAnnouncement(id, { is_active: !currentActive });
      setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, is_active: !currentActive } : a));
      toast.success(!currentActive ? '公開しました' : '非公開にしました');
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAnnouncement(id);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast.success('削除しました');
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Megaphone className="h-5 w-5" />新しいお知らせを作成
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="お知らせメッセージを入力..."
              className="flex-1 min-h-[60px]"
              data-testid="announcement-input"
            />
            <Button onClick={handleCreate} disabled={saving} className="self-end">
              <Plus className="h-4 w-4 mr-1" />{saving ? '作成中...' : '作成'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {announcements.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">お知らせはありません</p>
      ) : (
        <div className="space-y-3" data-testid="announcement-list">
          {announcements.map(a => (
            <Card key={a.id} className={`${a.is_active ? 'border-emerald-200' : 'border-slate-200 opacity-60'}`}>
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <p className="text-sm whitespace-pre-wrap">{a.message}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {a.created_at ? format(new Date(a.created_at), 'yyyy/MM/dd HH:mm', { locale: ja }) : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{a.is_active ? '公開中' : '非公開'}</span>
                      <Switch
                        checked={a.is_active}
                        onCheckedChange={() => handleToggle(a.id, a.is_active)}
                      />
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => handleDelete(a.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
